import { create } from 'zustand';
import api from '../lib/axios';
import { queryClient } from '../lib/queryClient';

export type ImportJobStatus = 'analyzing' | 'done' | 'error';

export interface ImportJob {
  fileName: string;
  journalId: string;
  status: ImportJobStatus;
  importId?: number;
  error?: string;
  startedAt: number;
}

interface ImportJobState {
  job: ImportJob | null;
  start: (file: File, journalId: string, bankAccount?: string) => Promise<void>;
  clear: () => void;
}

// Owns the statement-import request at module scope so the analysis keeps
// running (and stays observable) even after the user navigates away.
export const useImportJobStore = create<ImportJobState>((set, get) => ({
  job: null,

  start: async (file, journalId, bankAccount) => {
    if (get().job?.status === 'analyzing') return;
    set({
      job: { fileName: file.name, journalId, status: 'analyzing', startedAt: Date.now() },
    });
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('journalId', journalId);
      if (bankAccount) form.append('bankAccount', bankAccount);
      // Upload + parse only: returns immediately while Claude classifies in the background.
      const { data } = await api.post('/imports/statements', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const importId: number = data.importId;
      set((s) => ({ job: s.job ? { ...s.job, importId } : null }));
      queryClient.invalidateQueries({ queryKey: ['imports'] });

      // Poll until the background analysis leaves the ANALYZING state.
      const finish = () => {
        set((s) => ({ job: s.job ? { ...s.job, status: 'done', importId } : null }));
        queryClient.invalidateQueries({ queryKey: ['imports'] });
      };
      const poll = async () => {
        try {
          const { data: detail } = await api.get(`/imports/statements/${importId}`);
          if (detail?.import?.status === 'ANALYZING') {
            setTimeout(poll, 2500);
          } else {
            finish();
          }
        } catch {
          // Analysis keeps running server-side; surface the lines anyway.
          finish();
        }
      };
      setTimeout(poll, 2500);
    } catch (e: any) {
      const message = e?.response?.data?.message ?? e?.message ?? 'error';
      set((s) => ({ job: s.job ? { ...s.job, status: 'error', error: message } : null }));
    }
  },

  clear: () => set({ job: null }),
}));
